import { formatLocalShortRange, formatLocalDayHm } from "@/utils/general-functions";
import { todayLocalDateString, toDateParam } from './dailyDate';

// Day math, day navigation and local-time formatting for the Daily page. Mixed into Daily.vue: reads its state (dateParam, nowMs, calMatchesList ...) through `this`.
export default {
  computed: {
    dayStart() {
      // Local midnight for dateParam (no trailing Z - the browser parses a Date-only/no-
      // offset string as local time, which is exactly what's wanted here).
      return new Date(`${this.dateParam}T00:00:00`);
    },
    dayEnd() {
      return new Date(this.dayStart.getTime() + 24 * 60 * 60 * 1000);
    },
    dayChoices() {
      const y = this.dayStart.getFullYear();
      const m = this.dayStart.getMonth();
      const d = this.dayStart.getDate();
      const today = todayLocalDateString();
      const out = [];
      for (let off = -3; off <= 7; off++) {
        const date = new Date(y, m, d + off);
        const param = toDateParam(date);
        out.push({
          param,
          current: off === 0,
          today: param === today,
          label: date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
        });
      }
      return out;
    },
    dayLabelText() {
      return this.dayStart.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    },
    isToday() {
      return this.dateParam === todayLocalDateString();
    },
    timezoneLabel() {
      const offsetMin = -new Date().getTimezoneOffset();
      const sign = offsetMin >= 0 ? '+' : '-';
      const abs = Math.abs(offsetMin);
      const hh = String(Math.floor(abs / 60)).padStart(2, '0');
      const mm = String(abs % 60).padStart(2, '0');
      return `UTC${sign}${hh}:${mm}`;
    }
  },
  methods: {
    // --- Day math / navigation ---------------------------------------------------------
    effectiveEnd(match) {
      return match.endTime || match.startTime;
    },
    touchesDay(match) {
      if (!match.startTime) return false;
      const start = new Date(match.startTime);
      const end = new Date(this.effectiveEnd(match));
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
      return start < this.dayEnd && end > this.dayStart;
    },
    crossesMidnight(match) {
      const end = new Date(this.effectiveEnd(match));
      return !isNaN(end.getTime()) && end > this.dayEnd;
    },
    matchPhase(match) {
      const start = new Date(match.startTime).getTime();
      const end = new Date(this.effectiveEnd(match)).getTime();
      if (isNaN(start)) return '';
      if (start <= this.nowMs && this.nowMs < end) return 'live';
      return start > this.nowMs ? 'upcoming' : '';
    },
    pickDay(dateStr) {
      this.dayMenuOpen = false;
      if (dateStr !== this.dateParam) this.goToDate(dateStr);
    },
    closeDayMenu() {
      this.dayMenuOpen = false;
    },
    goToDate(dateStr) {
      this.$router.push({ name: 'Daily', query: { date: dateStr } });
    },
    goPrevDay() {
      this.goToDate(toDateParam(new Date(this.dayStart.getTime() - 24 * 60 * 60 * 1000)));
    },
    goNextDay() {
      this.goToDate(toDateParam(new Date(this.dayStart.getTime() + 24 * 60 * 60 * 1000)));
    },
    goToday() {
      this.goToDate(todayLocalDateString());
    },
    // --- Local-time display (deliberately NOT UTC - see dayStart's own comment) --------
    formatLocalTime(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    },
    formatLocalDateShort(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    },
    formatLocalDateTime(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return `${this.formatLocalDateShort(iso)} ${this.formatLocalTime(iso)}`;
    },
    formatLocalHm(ms) {
      return formatLocalDayHm(ms);
    },
    // Local short range - labels a match in cross-match conflict messages.
    formatShortRange(match) {
      return formatLocalShortRange(match.startTime, match.endTime);
    },
    matchFinished(match) {
      const end = new Date(this.effectiveEnd(match)).getTime();
      return !isNaN(end) && end <= this.nowMs;
    },
    // Sources and events stay editable until the match has finished.
    matchEditable(match) {
      return !this.matchFinished(match);
    }
  }
};
